import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { noticeList, readNotice } from "api/notice";
import styles from "./notice.module.less";
import CommonNavBar from "../../components/CommonNavBar";
import { Toast, Badge } from "react-vant";
import DSBridge from "dsbridge";

function Notice() {
  const navigator = useNavigate();
  const [rawMessages, setRawMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef(null);

  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  // 同步状态供 IntersectionObserver 回调中读取
  loadingRef.current = loading;
  hasMoreRef.current = hasMore;

  const fetchData = useCallback(async (isReset = false) => {
    if (loadingRef.current) return;
    if (!isReset && !hasMoreRef.current) return;

    const targetPage = isReset ? 1 : pageRef.current;

    try {
      setLoading(true);
      loadingRef.current = true;
      const res = await noticeList(targetPage);
      if (res.code === 200) {
        const newData = res.data?.data || [];
        if (newData.length === 0) {
          setHasMore(false);
          hasMoreRef.current = false;
        } else {
          setRawMessages((prev) => (isReset ? newData : [...prev, ...newData]));
          pageRef.current = targetPage + 1;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // 按日期全局分组数据（修复分页时相同日期被拆分的问题）
  const groupedItems = useMemo(() => {
    const groupedData = rawMessages.reduce((groups, message) => {
      if (!message.send_time) return groups;
      const date = message.send_time.split(" ")[0];
      const time = message.send_time.split(" ")[1];
      if (!groups[date]) {
        groups[date] = {
          date: date,
          messages: [],
        };
      }
      groups[date].messages.push({
        ...message,
        send_time: time,
        contentType: message.content_type,
      });
      return groups;
    }, {});

    // 转换为数组格式并按日期倒序
    return Object.values(groupedData).sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  }, [rawMessages]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loadingRef.current && hasMoreRef.current) {
          fetchData();
        }
      },
      { threshold: 0.1 },
    );
    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }
    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [fetchData]);

  const readState = (item, type) => {
    readNotice(item.message_id).then(() => {
      // 本地同步更新已读状态，无须重新刷新列表
      setRawMessages((prev) =>
        prev.map((msg) =>
          msg.message_id === item.message_id ? { ...msg, is_read: 1 } : msg,
        ),
      );

      if (type === 1) {
        navigator("/main/course/0");
      } else if (type === 7) {
        navigator(`/mine/myGift`);
      } else if (type === 8) {
        navigator(`/mine/medalDetail?category_id=${item.category_id}`);
      } else {
        if (item.contentType === 1) {
          navigator(`/main/notice/detail?message_id=${item.message_id}`);
        } else if (item.message_link) {
          DSBridge.call(
            "route.navigation",
            {
              route: "webview",
              url: item.message_link,
              title: "消息详情",
            },
            (back) => {
              console.log(back);
            },
          );
        }
      }
    });
  };

  const clearAllRead = () => {
    readNotice(0).then((res) => {
      if (res.code === 200) {
        Toast.success("清除成功");
        setHasMore(true);
        hasMoreRef.current = true;
        fetchData(true);
      }
    });
  };

  const getIconUrl = (type) => {
    switch (type) {
      case 1:
        return require("@/images/notice/m_icon1.png");
      case 2:
        return require("@/images/notice/m_icon2.png");
      case 3:
        return require("@/images/notice/m_icon3.png");
      case 4:
        return require("@/images/notice/m_icon4.png");
      case 5:
        return require("@/images/notice/m_icon5.png");
      case 6:
        return require("@/images/notice/m_icon6.png");
      case 7:
        return require("@/images/notice/m_icon7.png");
      case 8:
        return require("@/images/notice/m_icon8.png");
      default:
        return null;
    }
  };

  return (
    <div className={styles["infinite-scroll-container"]}>
      <CommonNavBar
        title={"消息中心"}
        backgroundColor={"transparent"}
        onClickBack={() => {
          navigator(-1);
        }}
      />
      <div className={styles["clear-read"]} onClick={() => clearAllRead()}>
        清除未读
      </div>
      <img
        className={styles["right-banner"]}
        src={require("@/images/mine/img_integration_top_bg.png")}
        alt=""
      />
      <div className={styles["items-list"]}>
        {groupedItems.map(({ date, messages }, index) => (
          <div key={index} className={styles["item"]}>
            <div className={styles["time"]}>{date}</div>
            {messages.map((item, idx) => {
              const iconUrl = getIconUrl(item.type);
              return (
                <div key={idx} className={styles["card"]}>
                  <div className={styles["title"]}>
                    <div className={styles["title-left"]}>
                      {item.is_read === 0 && iconUrl ? (
                        <Badge offset={["90%", 0]} dot>
                          <img
                            className={styles["title-icon"]}
                            src={iconUrl}
                            alt=""
                          />
                        </Badge>
                      ) : iconUrl ? (
                        <img
                          className={styles["title-icon"]}
                          src={iconUrl}
                          alt=""
                        />
                      ) : null}

                      <div className={styles["title-title"]}>
                        {item.message_title}
                      </div>
                    </div>
                    <div className={styles["title-time"]}>{item.send_time}</div>
                  </div>
                  <div className={styles["desc"]}>{item.message_desc}</div>
                  <div className={styles["btn"]}>
                    {(item.type === 1 || item.type === 2) && (
                      <div
                        className={styles["btn1"]}
                        onClick={() => readState(item, 1)}
                      >
                        去上课
                      </div>
                    )}
                    {item.type === 7 && (
                      <div
                        className={styles["btn1"]}
                        onClick={() => readState(item, 7)}
                      >
                        立即使用
                      </div>
                    )}
                    {item.type === 8 && (
                      <div
                        className={styles["btn1"]}
                        onClick={() => readState(item, 8)}
                      >
                        查看勋章
                      </div>
                    )}
                    <div
                      className={styles["btn2"]}
                      onClick={() => readState(item, 2)}
                    >
                      查看详情
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={observerTarget} className={styles["observer-target"]}>
          {loading && <div className="loading">加载中...</div>}
          {!hasMore && groupedItems.length > 0 && (
            <div className="no-more">没有更多了</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Notice;
